'use strict';

/**
 * Base class for a Collection
 *
 * @category   Collection
 * @module     base
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var Backbone = require('backbone');
var FilteredCollection = require('base/Colido.FilteredCollection');

var sortExtension = require('base/mixins/sort');
var searchExtension = require('base/mixins/search');
var objectsCollectionExtension = require('base/mixins/objectsCollection');
var patchExtension = require('base/mixins/patch');
var deleteExtension = require('base/mixins/delete');

var Collection = Backbone.Collection.extend({
    filterComponents: [],

    filterFieldsMap: {},

    url: function() {
        var endpoint = _.result(this, 'api');

        return App.getApiUrl(endpoint);
    },

    // Like collection.set() but clears `changed`` attributes after setting them
    // Without this updating data via websocket sets `changed` flag for the attributes,
    // resulting in collection PATCHes to servers transmitting the data again.
    // This led to loosing images in the past, because the backend can only handle images[id,id] instead of images{123: {..imagedata..}}
    setFromWebsocket: function(models, options) {
        var updatedModels = this.set(models, options);

        // Calling set twice removes the native `changed` values
        // Maybe this is a performance hit for large changes, but the docu says model.changed should not be set manually, so I fear sideeffects setting it directly.
        // But then on the other hand the WebSocket sends every update twice anyways.
        this.set(models, options);

        // This clears the models custom changed data for the patchExtension
        updatedModels = Array.isArray(updatedModels) ? updatedModels : [updatedModels];
        _.each(updatedModels, function(model) {
            model.clearUpdatedAttributes();
        });

        return updatedModels;
    },

    /**
     * Returns a FilteredCollection
     * @param {string|function|array} filter The filter to be applied.
     * Can a be string like "name:abc/color:red" or a function like filter(model) { return model.get('id') === 1 }.
     * Or an array consiting of both functions and/or strings.
     *
     * @return {Colido.FilteredCollection} The filtered collection
     */
    getFilteredCollection: function(filter) {
        filter = this._buildFilterFunction(filter);

        var MyFilteredCollection = FilteredCollection.extend({model: this.model});
        var filteredCollection = new MyFilteredCollection(null, {collection: this});

        if (_.isFunction(filter)) {
            filteredCollection.setFilter(filter);
            filteredCollection.originalFilter = filter;
        }

        return filteredCollection;
    },

    /**
     * Returns the length of a collection with applied filter
     * @param {string|function|array} filter The filter to be applied.
     * Can a be string like "name:abc/color:red" or a function like filter(model) { return model.get('id') === 1 }.
     * Or an array consiting of both functions and/or strings.
     *
     * @return {integer} The filtered collection length
     */
    getFilteredCount: function(filter) {
        filter = this._buildFilterFunction(filter);

        return this.filter(filter).length;
    },

    /**
     *
     * @param filter
     * @returns {*}
     * @private
     */
    _buildFilterFunction: function(filter) {
        filter = _.isString(filter) || _.isFunction(filter) ? [filter] : filter;

        var filterFunctions = [];

        if (_.isArray(filter)) {
            filter.forEach(function(filter){
                if (_.isString(filter)) {
                    filter = this._parseFilterString(filter);
                }

                if (_.isFunction(filter)) {
                    filterFunctions.push(filter);
                }
            }.bind(this));
        }

        if (filterFunctions.length) {
            return function(model) {
                var returnValue = true;
                for(var i = 0; i < filterFunctions.length; i++) {
                    returnValue = returnValue && filterFunctions[i](model);
                }

                return returnValue;
            };
        }

        return null;
    },

    /**
     *
     * @param filterString
     * @returns {*}
     * @private
     */
    _parseFilterString: function(filterString) {
        //if filter is string with even number of params transform to function
        if (_.isString(filterString)) {
            var filter = filterString.split('/');

            if (filter.length % 2 === 0) {
                return function(model) {
                    var returnValue = true;

                    for(var i = 0; i < filter.length; i++) {
                        if (i % 2 === 0) {
                            var filterKeys = filter[i].split(':');
                            var requestedValue = filter[i + 1];
                            var filterKey = (this.collection ? this.collection.filterFieldsMap[filterKeys[0]] : this.filterFieldsMap[filterKeys[0]]) || filterKeys[0];
                            var filterSubKey = filterKeys.length > 1 ? filterKeys[1] : null;

                            var criteria = {
                                filterKey: filterKey,
                                filterSubKey: filterSubKey,
                                requestedValue: requestedValue,
                            };

                            returnValue = returnValue && this._getFilterRule(model, criteria);
                        }
                    }

                    return returnValue;
                }.bind(this);
            }
        }

        return null;
    },

    //overload this in your collection
    _getFilterRule: function(model, criteria) {
        var modelValue = model.get(criteria.filterKey);

        if (criteria.filterSubKey && modelValue && modelValue.hasOwnProperty(criteria.filterSubKey)) {
            modelValue = modelValue[criteria.filterSubKey];
        }

        if (_.isArray(modelValue)) {
            return _.contains(modelValue, criteria.requestedValue);
        }

        return modelValue == criteria.requestedValue;
    },

    isValueInRange: function(value, rangeString) {
        if (_.isNumber(value) && !isNaN(value)) {
            var range = rangeString.split('~');

            if (range.length === 1) {
                return value === parseFloat(range[0]);
            }

            if (range.length === 2) {
                var startValue = parseFloat(range[0]);
                var endValue = parseFloat(range[1]);

                if (!isNaN(startValue) && !isNaN(endValue)) {
                    return value >= startValue && value <= endValue;
                }

                if (!isNaN(startValue)) {
                    return value >= startValue;
                }

                if (!isNaN(endValue)) {
                    return value <= endValue;
                }
            }
        }

        return false;
    },

    isValueInList: function(values, listString) {
        var list = _.isString(listString) ? listString.split(',') : [];
        values = _.isArray(values) ? values.map(function(value){ return value + ''; }) : [values + ''];

        return _.intersection(values, list).length > 0;
    },
});

_.extend(Collection.prototype, sortExtension, searchExtension, objectsCollectionExtension, patchExtension, deleteExtension);

module.exports = Collection;